import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data } from '@angular/router';

import { RelatorioFinanceiroApiService } from '../services/relatorio-financeiro-api.service';
import { MessageService } from '@services/message.service';
import { TipoBuscaEnum, OpcaoTipoBusca, OPCOES_TIPO_BUSCA } from '../models/tipo-busca.enum';
import { ConsultaLancamentosInput } from '../models/consulta-lancamentos-input';
import { ConsultaLancamentosOutput } from '../models/consulta-lancamentos-output';
import { SelecaoBusca } from '../models/selecao-busca';
import { TipoLancamento } from '@models/tipo-lancamento';
import { RelatorioCaixaListItem } from '../../configurar-relatorio-caixa/models/relatorio-caixa-list-item';
import { CategoriaRelatorioListItem } from '../models/categoria-relatorio-list-item';
import { DuploCliquePizzaEvent, DuploCliqueBarraEvent } from './relatorio-graficos/relatorio-graficos.component';
import { Permissao } from '@models/enums/permissao';

@Component({
  selector: 'app-relatorio-financeiro',
  templateUrl: './relatorio-financeiro.component.html',
  styleUrl: './relatorio-financeiro.component.scss',
  standalone: false,
})
export class RelatorioFinanceiroComponent implements OnInit {

  form: FormGroup;
  resultado: ConsultaLancamentosOutput | null = null;

  selecaoBuscaItens: SelecaoBusca[] = [];
  selecaoBuscaLabel: string = "Busca";
  contasCaixa: SelecaoBusca[] = [];
  tiposLancamento: TipoLancamento[] = [];
  relatoriosCaixa: RelatorioCaixaListItem[] = [];
  categoriasCaixa: CategoriaRelatorioListItem[] = [];

  readonly opcoesTipoBusca: OpcaoTipoBusca[] = OPCOES_TIPO_BUSCA;
  readonly tipoBuscaEnum = TipoBuscaEnum;

  abaAtiva = '0';

  protected get permissaoEditarRelatorioCaixa() { return Permissao.EDITAR_RELATORIO_CAIXA; }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private relatorioFinanceiroApiService: RelatorioFinanceiroApiService,
    private messageService: MessageService,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      tipoBusca: [OPCOES_TIPO_BUSCA[0], Validators.required],
      idSelecionado: [{ value: null, disabled: true }, Validators.required],
      dataInicio: [null],
      dataFim: [null],
    });

    this.route.data.subscribe((data: Data) => {
      const dados = data['relatorioFinanceiro'];
      this.contasCaixa = dados.contasCaixa;
      this.tiposLancamento = dados.tiposLancamento;
      this.relatoriosCaixa = dados.relatoriosCaixa;
      this.categoriasCaixa = dados.categoriasCaixa;
      this.onTipoBuscaChange();
      this.aplicarQueryParams();
    });
  }


  get tipoBuscaControl() { return this.form.get('tipoBusca'); }
  get idSelecionadoControl() { return this.form.get('idSelecionado'); }
  get dataInicioControl() { return this.form.get('dataInicio'); }
  get dataFimControl() { return this.form.get('dataFim'); }

  get tipoBuscaSelecionado(): TipoBuscaEnum | null {
    return (this.tipoBuscaControl?.value as OpcaoTipoBusca | null)?.value ?? null;
  }

  get temResultado(): boolean {
    return this.resultado !== null;
  }

  public onTipoBuscaChange(): void {
    this.idSelecionadoControl?.reset(null);

    switch (this.tipoBuscaSelecionado) {
      case TipoBuscaEnum.Conta:
        this.selecaoBuscaItens = this.contasCaixa;
        this.selecaoBuscaLabel = 'Conta';
        break;
      case TipoBuscaEnum.TipoLancamento:
        this.selecaoBuscaItens = this.tiposLancamento;
        this.selecaoBuscaLabel = 'Tipo de Lançamento';
        break;
      case TipoBuscaEnum.RelatorioCaixa:
        this.selecaoBuscaItens = this.relatoriosCaixa;
        this.selecaoBuscaLabel = 'Relatório';
        break;
      case TipoBuscaEnum.CategoriaRelatorio:
        this.selecaoBuscaItens = this.categoriasCaixa;
        this.selecaoBuscaLabel = 'Categoria do Relatório';
        break;
      default:
        this.selecaoBuscaItens = [];
        this.selecaoBuscaLabel = 'Busca';
        break;
    }

    if (this.tipoBuscaSelecionado === null) {
      this.form.get('idSelecionado')?.disable();
    } else {
      this.form.get('idSelecionado')?.enable();
    }
  }

  public buscar(): void {
    if (!this.validarFormulario()) return;

    const filtro = this.montarFiltro();
    this.abaAtiva = '0';
    this.relatorioFinanceiroApiService.consultarLancamentos(filtro).subscribe({
      next: (output) => {
        if (output.lancamentos.length === 0) {
          this.relatorioVazioWarning();
          return;
        }
        output.lancamentos.forEach(l => {
          l.dataFormatada = new Date(l.data).toLocaleDateString('pt-BR');
        });
        this.resultado = output;
      },
      error: () => this.relatorioVazioWarning(),
    });
  }

  private relatorioVazioWarning(): void {
    this.resultado = null;
    this.messageService.showToastWarning('Nenhum lançamento encontrado para os filtros selecionados.');
  }

  private validarFormulario(): boolean {
    if (!this.tipoBuscaControl?.value) {
      this.messageService.showToastWarning('Selecione o tipo de busca.');
      return false;
    }

    if (!this.idSelecionadoControl?.value) {
      this.messageService.showToastWarning('Selecione uma conta ou tipo.');
      return false;
    }

    return true;
  }

  private montarFiltro(): ConsultaLancamentosInput {
    const idBuscado = (this.idSelecionadoControl?.value as SelecaoBusca).id;

    return {
      tipoBusca: this.tipoBuscaSelecionado!,
      idBuscado,
      dataInicio: this.dataInicioControl?.value,
      dataFim: this.dataFimControl?.value,
    };
  }

  public buscarBarraChart(ev: DuploCliqueBarraEvent): void {
    const qp: string[] = [];
    const { tipoBusca, idBuscado } = this.resultado!.parametrosBusca;
    qp.push(`tipoBusca=${tipoBusca}`);
    qp.push(`idBuscado=${idBuscado}`);
    qp.push(`dataInicio=${ev.dataInicio}`);
    qp.push(`dataFim=${ev.dataFim}`);
    window.open(`${window.location.origin}/relatorio-financeiro?${qp.join('&')}`, '_blank');
  }

  public buscarCategoriaChart(ev: DuploCliquePizzaEvent): void {
    const qp: string[] = [`tipoBusca=${ev.tipoBusca}`, `idBuscado=${ev.id}`];
    const { dataInicio, dataFim } = this.resultado!.parametrosBusca;
    if (dataInicio) qp.push(`dataInicio=${this.formatarData(new Date(dataInicio))}`);
    if (dataFim)    qp.push(`dataFim=${this.formatarData(new Date(dataFim))}`);

    window.open(`${window.location.origin}/relatorio-financeiro?${qp.join('&')}`, '_blank');
  }

  private aplicarQueryParams(): void {
    const qp = this.route.snapshot.queryParams;
    const tipoBusca = qp['tipoBusca'] != null ? Number(qp['tipoBusca']) as TipoBuscaEnum : null;
    const idBuscado = qp['idBuscado'] != null ? Number(qp['idBuscado']) : null;
    if (tipoBusca == null || idBuscado == null) return;

    const opcao = OPCOES_TIPO_BUSCA.find(o => o.value === tipoBusca);
    if (!opcao) return;

    this.form.get('tipoBusca')?.setValue(opcao);
    this.onTipoBuscaChange();

    let item: SelecaoBusca | undefined;
    switch (tipoBusca) {
      case TipoBuscaEnum.Conta:              item = this.contasCaixa.find(c => c.id === idBuscado); break;
      case TipoBuscaEnum.TipoLancamento:     item = this.tiposLancamento.find(t => t.id === idBuscado); break;
      case TipoBuscaEnum.RelatorioCaixa:     item = this.relatoriosCaixa.find(r => r.id === idBuscado); break;
      case TipoBuscaEnum.CategoriaRelatorio: item = this.categoriasCaixa.find(c => c.id === idBuscado); break;
    }
    if (!item) return;

    this.form.get('idSelecionado')?.setValue(item);
    if (qp['dataInicio']) this.form.get('dataInicio')?.setValue(this.parsarData(qp['dataInicio']));
    if (qp['dataFim'])    this.form.get('dataFim')?.setValue(this.parsarData(qp['dataFim']));

    this.buscar();
  }

  private formatarData(data: Date): string {
    return data.toISOString().split('T')[0];
  }

  private parsarData(iso: string): Date {
    const [ano, mes, dia] = iso.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }
}
